import type { ElementPatch, TableElement } from '@/components/canvas/document/publicTypes';
import { TABLE_MIN_COL_WIDTH, TABLE_MIN_ROW_HEIGHT } from '@/components/canvas/shared/constants/legacyConfig';

/** 在满足每项 ≥ minPer 且总和 = newTotal 的前提下，按旧值比例分配。 */
export function distributeWithMin(oldValues: number[], newTotal: number, minPer: number): number[] {
  const n = oldValues.length;
  if (n === 0) return [];
  const baseTotal = n * minPer;
  if (newTotal <= baseTotal) return Array(n).fill(minPer);
  const remainder = newTotal - baseTotal;
  const weights = oldValues.map((v) => Math.max(0, v - minPer));
  const sumW = weights.reduce((a, b) => a + b, 0);
  let out: number[];
  if (sumW <= 0) {
    out = Array(n).fill(minPer + remainder / n);
  } else {
    out = oldValues.map((_, i) => minPer + (remainder * weights[i]) / sumW);
  }
  const sum = out.reduce((a, b) => a + b, 0);
  if (sum !== newTotal && n > 0) {
    out[0] += newTotal - sum;
    out[0] = Math.max(minPer, out[0]);
  }
  return out;
}

export function resolveTableSizePatch(
  table: TableElement,
  patch: ElementPatch,
): ElementPatch {
  const dataPatch = patch.data as Partial<TableElement['data']> | undefined;
  const rows = Math.max(1, dataPatch?.rows ?? table.data.rows);
  const cols = Math.max(1, dataPatch?.cols ?? table.data.cols);
  const curW = table.size.width;
  const curH = table.size.height;
  const rowHeightsPatch = dataPatch?.rowHeights;
  const colWidthsPatch = dataPatch?.colWidths;
  const hasExplicitRowHeights = rowHeightsPatch != null && rowHeightsPatch.length >= rows;
  const hasExplicitColWidths = colWidthsPatch != null && colWidthsPatch.length >= cols;
  const newW = hasExplicitColWidths
    ? colWidthsPatch!.slice(0, cols).reduce((a, b) => a + b, 0)
    : (patch.size?.width ?? curW);
  const newH = hasExplicitRowHeights
    ? rowHeightsPatch!.slice(0, rows).reduce((a, b) => a + b, 0)
    : (patch.size?.height ?? curH);
  const curRowHeights =
    table.data.rowHeights && table.data.rowHeights.length >= rows
      ? table.data.rowHeights.slice(0, rows)
      : Array.from({ length: rows }, () => (rows > 0 ? curH / rows : 0));
  const curColWidths =
    table.data.colWidths && table.data.colWidths.length >= cols
      ? table.data.colWidths.slice(0, cols)
      : Array.from({ length: cols }, () => (cols > 0 ? curW / cols : 0));
  return {
    ...patch,
    size: { ...patch.size, width: newW, height: newH },
    data: {
      ...dataPatch,
      rowHeights: hasExplicitRowHeights
        ? rowHeightsPatch!.slice(0, rows)
        : distributeWithMin(curRowHeights, newH, TABLE_MIN_ROW_HEIGHT),
      colWidths: hasExplicitColWidths
        ? colWidthsPatch!.slice(0, cols)
        : distributeWithMin(curColWidths, newW, TABLE_MIN_COL_WIDTH),
    },
  };
}
